'use client'

import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { palettes, DEFAULT_PALETTE, RothkoPalette } from '@/lib/palettes'

type PaletteContextType = {
  currentPalette: RothkoPalette
  paletteName: string
  setPalette: (name: string) => void
  paletteNames: string[]
}

const PaletteContext = createContext<PaletteContextType | undefined>(undefined)

export function PaletteProvider({ children }: { children: ReactNode }) {
  const [paletteName, setPaletteName] = useState(DEFAULT_PALETTE)

  const currentPalette = palettes[paletteName] || palettes[DEFAULT_PALETTE]
  const paletteNames = Object.keys(palettes)

  useEffect(() => {
    // Apply palette as CSS custom properties
    const root = document.documentElement
    const p = currentPalette

    // Hero colors
    root.style.setProperty('--hero-ground', p.hero.ground)
    root.style.setProperty('--hero-top-1', p.hero.topField[0])
    root.style.setProperty('--hero-top-2', p.hero.topField[1])
    root.style.setProperty('--hero-top-3', p.hero.topField[2])
    root.style.setProperty('--hero-top-4', p.hero.topField[3])
    root.style.setProperty('--hero-bottom-1', p.hero.bottomField[0])
    root.style.setProperty('--hero-bottom-2', p.hero.bottomField[1])
    root.style.setProperty('--hero-bottom-3', p.hero.bottomField[2])
    root.style.setProperty('--hero-haze-1', p.hero.haze[0])
    root.style.setProperty('--hero-haze-2', p.hero.haze[1])
    root.style.setProperty('--hero-haze-3', p.hero.haze[2])
    root.style.setProperty('--hero-haze-4', p.hero.haze[3])

    // Accent colors
    root.style.setProperty('--accent-primary', p.accents.primary)
    root.style.setProperty('--accent-secondary', p.accents.secondary)
    root.style.setProperty('--accent-warm', p.accents.warm)
    root.style.setProperty('--accent-deep', p.accents.deep)
    root.style.setProperty('--accent-cream', p.accents.cream)
  }, [currentPalette])

  const setPalette = (name: string) => {
    if (palettes[name]) {
      setPaletteName(name)
    }
  }

  return (
    <PaletteContext.Provider value={{ currentPalette, paletteName, setPalette, paletteNames }}>
      {children}
    </PaletteContext.Provider>
  )
}

export function usePalette() {
  const context = useContext(PaletteContext)
  if (context === undefined) {
    throw new Error('usePalette must be used within a PaletteProvider')
  }
  return context
}
